import { useSelector } from 'react-redux'
import { AllState } from '../../../../store'
import { APPLICATION_FUNCTION_EXTERNAL_ID } from '../../../pages'
import { PageContext } from '../../../../lib/functions/ai'

// 画面種別（externalId）→ pageContext.screen の正規化語彙。
// ai-agent-context-design.md の「渡すべき情報一覧 #2」参照。ここに載っていない画面は
// pageContext 自体を省略する（エージェント側は screen 未知の画面を想定していないため）。
// 対応する日本語ラベルはエージェント側（flagxs-ai-agent の main.py の _SCREEN_LABELS_JA）に
// 定義されており、片側だけ画面を足すとラベルが付かないまま表示されるため両方に追加すること。
const SCREEN_BY_EXTERNAL_ID: Partial<
  Record<APPLICATION_FUNCTION_EXTERNAL_ID, string>
> = {
  [APPLICATION_FUNCTION_EXTERNAL_ID.PROJECTPLAN_NEW_EDIT]: 'projectPlan',
  [APPLICATION_FUNCTION_EXTERNAL_ID.MY_WBS_ITEMS]: 'myTasks',
  [APPLICATION_FUNCTION_EXTERNAL_ID.PROJECTS_SEARCH]: 'projectList',
}

// このscreenに紐づく画面はプロジェクトに紐づく（pageContext.projectを付与しうる）。
// 現状 projectPlan のみだが、対応画面が増えたらここに追加する。
const SCREENS_WITH_PROJECT = new Set<string>(['projectPlan'])

/**
 * 現在の画面状態からAIエージェントに渡す pageContext を組み立てるフック。
 * 埋め込みパネル（AiAgentChat.tsx）から利用する。チャット専用ページは周辺文脈を
 * 持たないため、このフックを使わず固定の pageContext（screen: 'chat'）を用いる。
 */
export const usePageContext = (): PageContext | undefined => {
  const currentExternalId = useSelector(
    (state: AllState) => state.appFunction.currentExternalId
  )
  const project = useSelector((state: AllState) => state.project.current)

  const screen = currentExternalId
    ? SCREEN_BY_EXTERNAL_ID[
        currentExternalId as APPLICATION_FUNCTION_EXTERNAL_ID
      ]
    : undefined
  if (!screen) {
    // 判定できない画面では pageContext 自体を省略する。
    return undefined
  }

  if (!SCREENS_WITH_PROJECT.has(screen) || !project) {
    return { screen }
  }

  // TODO: rootWbsItemは表示ルート状態の取得経路が未整備のため未実装
  // (ProjectPlanNew の表示ルートWBSは src/view/pages/ProjectPlanNew/hooks/projectPlanNewData.ts
  // 内の useRef ローカル状態であり、Reduxストア等の画面外から参照できる経路がない)
  return {
    screen,
    project: {
      code: project.code,
      name: project.displayName,
    },
  }
}
